"use client";
import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { ExtractedData, SectionScores } from "../DocumentUploadOverlay";
import { schoolBoard } from "../Helper";
import SelectInput from "../ai-course-finder/SelectInput";
import { parseDate } from "@/utils/parseDate";

interface ConfirmExtractedDetailsProps {
	documentName: string;
	extractedData: ExtractedData;
	editErrors: Record<string, string>;
	isLoading: boolean;
	onChange: (field: string, value: string) => void;
	onConfirm: () => void;
	onClose: () => void;
	inline?: boolean;
}

const ConfirmExtractedDetails: React.FC<ConfirmExtractedDetailsProps> = ({
	documentName,
	extractedData,
	editErrors,
	onChange,
	isLoading,
	onConfirm,
	onClose,
	inline = false,
}) => {
	const docType = documentName
		.toLowerCase()
		.trim()
		.replace(/\s+/g, "_")
		.replace(/[^a-z0-9_]/g, "");
	const isHigherQualificationDocument =
		(docType.includes("bachelor") ||
			docType.includes("master") ||
			docType.includes("phd") ||
			docType.includes("diploma") ||
			docType.includes("transcript")) &&
		!docType.includes("passport") &&
		!docType.includes("english") &&
		!docType.includes("moi") &&
		!docType.includes("10th") &&
		!docType.includes("12th");
	const [validationErrors, setValidationErrors] = React.useState<
		Record<string, string>
	>({});

	// Normalize test name for validation
	const normalizeTestName = (testName: string): string => {
		const normalized = testName.toUpperCase().trim();
		if (normalized.includes("IELTS")) return "IELTS";
		if (normalized.includes("PTE")) return "PTE";
		if (normalized.includes("TOEFL")) return "TOEFL";
		if (normalized.includes("DUOLINGO")) return "DUOLINGO";
		if (
			normalized.includes("I-DONT-HAVE-ANY") ||
			normalized.includes("NO TEST")
		)
			return "I-DONT-HAVE-ANY";
		return normalized;
	};

	// Validation function for passport fields
	const validatePassportField = (
		field: string,
		value: string,
	): string | null => {
		if (field === "passport_number") {
			const passportRegex = /^[A-Za-z][A-Za-z0-9][0-9]{6}$/;
			if (value === "") {
				return "Passport number is required";
			} else if (!passportRegex.test(value)) {
				return "Invalid passport number";
			}
		}

		if (field === "date_of_expiry") {
			if (value === "") {
				return "Expiry date is required";
			}
			// Check if expiry date is in the future
			const expiryDate = new Date(value);
			const currentDate = new Date();
			if (expiryDate <= currentDate) {
				return "Expiry date must be in the future";
			}
		}

		if (field === "nationality") {
			if (value === "") {
				return "Nationality is required";
			}
		}

		return null;
	};

	// Validation function for English proficiency scores
	const validateProficiencyScore = (
		value: string,
		testType: string,
		fieldName: string,
	): string | null => {
		if (value === "" || value === null || value === undefined) return null;

		const numScore = Number(value);
		const normalizedTest = normalizeTestName(testType);

		if (isNaN(numScore)) {
			return "Score must be a valid number";
		}

		switch (normalizedTest) {
			case "IELTS":
				if (numScore < 4 || numScore > 10) {
					return "Score must be between 4 and 10";
				}
				if (numScore % 1 !== 0 && numScore % 1 !== 0.5) {
					return "Only .5 decimals allowed (e.g., 6.5)";
				}
				return null;

			case "PTE":
				if (numScore < 10 || numScore > 90) {
					return "Score must be between 10 and 90";
				}
				return null;

			case "TOEFL":
				if (fieldName !== "overall") {
					if (numScore < 0 || numScore > 30) {
						return "Score must be between 0 and 30";
					}
				} else {
					if (numScore < 0 || numScore > 120) {
						return "Overall score must be between 0 and 120";
					}
				}
				return null;

			case "DUOLINGO":
				if (numScore < 10 || numScore > 160) {
					return "Score must be between 10 and 160";
				}
				return null;

			case "I-DONT-HAVE-ANY":
				if (numScore < 0 || numScore > 100) {
					return "Score must be between 0 and 100";
				}
				return null;

			default:
				return null;
		}
	};

	const validateAcademicField = (
		field: string,
		value: string,
		outOfValue?: string,
	): string | null => {
		const currentYear = new Date().getFullYear();

		const numValue = Number(value);

		switch (field) {
			case "cgpaOrPercentageValue":
				if (isNaN(numValue)) {
					return "Must be a number";
				} else if (!numValue) {
					return "Grade/Percentage is Required";
				}

				// Dynamic validation based on outOf value
				if (outOfValue) {
					const maxValue = Number(outOfValue);
					if (!isNaN(maxValue) && numValue > maxValue) {
						return `Cannot exceed ${maxValue}`;
					}
				} else if (numValue > 100) {
					return "Cannot exceed 100";
				}

				break;

			case "totalMarksObtained":
			case "outOf":
				if (isNaN(numValue)) {
					return "Must be a number";
				} else if (!numValue) {
					return `${field === "outOf" ? "Marks Out Of" : "Marks Obtained"} is Required`;
				}
				break;

			case "yearOfPassing":
				if (isNaN(numValue)) {
					return "Must be a number";
				} else if (!numValue) {
					return "Year of Passing is Required";
				} else if (numValue < 1950 || numValue > currentYear) {
					return `Year must be between 1950 and ${currentYear}`;
				}
				break;

			case "board":
			case "institution":
				if (value.trim() === "") {
					return `${field === "board" ? "Board" : "Institution"} is Required`;
				}
				break;

			default:
				break;
		}

		return null;
	};

	// Validation function for test name field
	const validateTestName = (value: string): string | null => {
		if (value === "") {
			return "Test name is required";
		}

		// Check if it's a valid test name (not a number)
		const validTests = ["IELTS", "PTE", "TOEFL", "DUOLINGO", "I-DONT-HAVE-ANY"];
		const normalizedValue = normalizeTestName(value);

		if (!validTests.includes(normalizedValue) && !isNaN(Number(value))) {
			return "Test name cannot be a number. Please enter a valid test name (IELTS, PTE, TOEFL, etc.)";
		}

		return null;
	};

	const getValidationError = (field: string, value: string): string | null => {
		if (field === "testName" || field === "test_type") {
			return validateTestName(value);
		}

		if (docType.includes("passport")) {
			return validatePassportField(field, value);
		}

		if (docType.includes("english") && extractedData?.test_type) {
			return validateProficiencyScore(value, extractedData.test_type, field);
		}

		if (docType.includes("10th") || docType.includes("12th")) {
			// Pass outOf value for cgpaOrPercentageValue validation
			const outOfValue =
				field === "cgpaOrPercentageValue"
					? extractedData?.outOf?.toString()
					: undefined;
			return validateAcademicField(field, value, outOfValue);
		}

		if (docType.includes("moi")) {
			return null;
		}

		// No strict validation for higher-qualification docs in this modal.
		return null;
	};

	// Validate single field and update validation errors state
	const validateSingleField = (field: string, value: string) => {
		const error = getValidationError(field, value);
		setValidationErrors((prev) => {
			const newErrors = { ...prev };
			if (error) {
				newErrors[field] = error;
			} else {
				delete newErrors[field];
			}
			return newErrors;
		});
	};

	// Validate all fields function
	const validateAllFields = (): boolean => {
		const errors: Record<string, string> = {};
		let hasErrors = false;

		// Get all fields to validate based on document type
		const fieldsToValidate = getFieldsToValidate();

		fieldsToValidate.forEach(({ field, value }) => {
			const error = getValidationError(field, value);
			if (error) {
				errors[field] = error;
				hasErrors = true;
			}
		});

		setValidationErrors(errors);
		return !hasErrors;
	};

	const getFieldsToValidate = () => {
		const fields: { field: string; value: string }[] = [];

		if (docType.includes("passport")) {
			const passportFields = [
				"passport_number",
				"date_of_expiry",
				"nationality",
			];
			passportFields.forEach((field) => {
				const value = extractedData?.[field as keyof ExtractedData];
				fields.push({
					field,
					value: value?.toString() || "",
				});
			});
		}

		if (docType.includes("10th") || docType.includes("12th")) {
			const academicFields = [
				"board",
				"yearOfPassing",
				"cgpaOrPercentageValue",
				"rollNumber",
				"institution",
				"totalMarksObtained",
				"outOf",
			];
			academicFields.forEach((field) => {
				const value = extractedData?.[field as keyof ExtractedData];
				fields.push({
					field,
					value: value?.toString() || "",
				});
			});
		}

		if (isHigherQualificationDocument) {
			// No strict validation needed for higher-qualification docs.
			return [];
		}

		if (docType.includes("english")) {
			// Test type
			fields.push({
				field: "testName",
				value: extractedData?.test_type || "",
			});

			// Overall score
			fields.push({
				field: "overall_score",
				value: extractedData?.overall_score?.toString() || "",
			});

			// Section scores
			if (extractedData?.section_scores) {
				const sectionScores = extractedData.section_scores as SectionScores;
				["listening", "reading", "writing", "speaking"].forEach((section) => {
					if (section in sectionScores) {
						fields.push({
							field: section,
							value: sectionScores[section] || "",
						});
					}
				});
			}
		}

		return fields;
	};

	// Enhanced onChange handler with real-time validation
	const handleFieldChange = (field: string, value: string) => {
		validateSingleField(field, value);
		onChange(field, value);
	};

	// Enhanced confirm handler with validation
	const handleConfirm = () => {
		const isValid = validateAllFields();
		if (isValid) {
			onConfirm();
		}
	};

	const formatDateForInput = (dateStr: string) => {
		if (!dateStr) return "";
		const date = parseDate(dateStr);
		if (date && !isNaN(date.getTime())) {
			// Use local date parts to avoid UTC timezone shift (e.g. IST +5:30 causing off-by-one)
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		}
		return dateStr;
	};

	const renderEditableField = (
		label: string,
		field: string,
		value: string,
		type: string = "text",
		isSelect: boolean = false,
	) => {
		const validationError = validationErrors[field];
		const editError = editErrors[field];
		const hasError = validationError || editError;

		const inputValue = type === "date" ? formatDateForInput(value) : value;

		return (
			<div className="mb-4 mt-4">
				<div className="relative">
					<span className="absolute top-[-8px] text-xs font-semibold left-3 bg-white px-2 text-gray-600 !z-30">
						{label}
					</span>
					<div className="relative">
						{isSelect && (field === "board" || field === "board_name") ? (
							<SelectInput
								placeHolder={false}
								className={`!bg-transparent !text-black border !h-[40px] !font-normal ${
									hasError
										? "!border-red-300 !bg-red-50 cursor-not-allowed"
										: "cursor-pointer !border-gray-300"
								}`}
								listClassName="!bg-white !text-black hover:!bg-gray-100"
								value={value}
								fieldName={field}
								title={`Select ${label}`}
								content={schoolBoard}
								onChange={(selectedValue) =>
									handleFieldChange(field, selectedValue)
								}
								fromProfile={false}
							/>
						) : (
							<input
								type={type}
								value={inputValue}
								onChange={(e) => handleFieldChange(field, e.target.value)}
								className={`w-full h-[45px] px-3 py-2 border rounded-md bg-transparent text-black ${
									hasError ? "border-red-300 bg-red-50" : "border-gray-300"
								}`}
								placeholder={`Enter ${label.toLowerCase()}`}
							/>
						)}
						{hasError && (
							<span className="text-red-500 text-xs mt-1 block">
								{validationError || editError}
							</span>
						)}
					</div>
				</div>
			</div>
		);
	};

	const getUpdateMessage = () => {
		if (docType.includes("passport"))
			return "Your passport details will be automatically updated in your profile when you confirm these details.";
		if (docType.includes("10th"))
			return "Your 10th standard academic details will be automatically updated in your profile when you confirm these details.";
		if (docType.includes("12th"))
			return "Your 12th standard academic details will be automatically updated in your profile when you confirm these details.";
		if (docType.includes("bachelor"))
			return "Your bachelor's degree details will be automatically updated in your profile when you confirm these details.";
		if (isHigherQualificationDocument)
			return "Please verify these extracted academic details before continuing with document submission.";
		if (docType.includes("english"))
			return "Your English proficiency test details will be automatically updated in your profile when you confirm these details.";
		return "";
	};

	// Check if there are any validation errors
	const hasValidationErrors = Object.keys(validationErrors).length > 0;

	// Determine grade label based on outOf value
	const getGradeLabel = () => {
		const outOf = extractedData?.outOf?.toString();
		if (outOf === "10") {
			return "CGPA";
		} else if (outOf === "100") {
			return "Percentage";
		}
		return "Grade/Percentage";
	};

	return (
		<div
			className={
				inline
					? "w-full"
					: "fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
			}>
			<div
				className={`bg-white w-full ${inline ? "" : "max-w-2xl rounded-lg shadow-xl p-6"} relative`}>
				<div className="flex justify-between items-center mb-6">
					<h2 className="text-xl font-semibold text-gray-800 ff_poppins">
						Confirm Extracted Details {inline && `for ${documentName}`}
					</h2>
				</div>

				<div className="mb-6 p-4 bg-gray-50 border rounded-lg  text-sm ff_poppins">
					<div className="flex items-start gap-2">
						<div>
							<strong className="block mb-1">Auto-Update Notice</strong>
							{getUpdateMessage()}
						</div>
					</div>
				</div>

				<div className="max-h-[400px] overflow-y-auto px-2">
					<div className="ff_poppins">
						{docType.includes("passport") && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{renderEditableField(
									"First Name",
									"given_name",
									typeof extractedData?.given_name === "string"
										? extractedData.given_name
										: "",
								)}
								{renderEditableField(
									"Last Name",
									"surname",
									typeof extractedData?.surname === "string"
										? extractedData.surname
										: "",
								)}
								{renderEditableField(
									"Passport Number",
									"passport_number",
									typeof extractedData?.passport_number === "string"
										? extractedData.passport_number
										: "",
								)}
								{renderEditableField(
									"Expiry Date",
									"date_of_expiry",
									typeof extractedData?.date_of_expiry === "string"
										? extractedData.date_of_expiry
										: "",
									"date",
								)}
								{renderEditableField(
									"Nationality",
									"nationality",
									typeof extractedData?.nationality === "string"
										? extractedData.nationality
										: "",
								)}
							</div>
						)}

						{(docType.includes("10th") || docType.includes("12th")) && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{renderEditableField(
									"Board Name",
									"board",
									typeof extractedData?.board === "string"
										? extractedData.board
										: "",
									"text",
									true,
								)}
								{renderEditableField(
									"Passing Year",
									"yearOfPassing",
									typeof extractedData?.yearOfPassing === "string"
										? extractedData.yearOfPassing
										: "",
									"number",
								)}
								{renderEditableField(
									"Percentage",
									"cgpaOrPercentageValue",
									typeof extractedData?.cgpaOrPercentageValue === "string"
										? extractedData.cgpaOrPercentageValue
										: "",
									"number",
								)}
								{renderEditableField(
									"Roll Number",
									"rollNumber",
									typeof extractedData?.rollNumber === "string"
										? extractedData.rollNumber
										: "",
								)}
								{renderEditableField(
									"School Name",
									"institution",
									typeof extractedData?.institution === "string"
										? extractedData.institution
										: "",
								)}
								{renderEditableField(
									"Marks Obtained",
									"totalMarksObtained",
									typeof extractedData?.totalMarksObtained === "string"
										? extractedData.totalMarksObtained
										: "",
									"number",
								)}
								{renderEditableField(
									"Marks Out Of",
									"outOf",
									typeof extractedData?.outOf === "string"
										? extractedData.outOf
										: "",
									"number",
								)}
							</div>
						)}

						{isHigherQualificationDocument && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{renderEditableField(
									"Student Name",
									"studentName",
									typeof extractedData?.studentName === "string"
										? extractedData.studentName
										: "",
								)}
								{renderEditableField(
									"Degree Name",
									"degreeName",
									typeof extractedData?.degreeName === "string"
										? extractedData.degreeName
										: "",
								)}
								{renderEditableField(
									"Institution Name",
									"institution",
									typeof extractedData?.institution === "string"
										? extractedData.institution
										: "",
								)}
								{renderEditableField(
									"Passing Year",
									"yearOfPassing",
									typeof extractedData?.yearOfPassing === "string"
										? extractedData.yearOfPassing
										: "",
									"number",
								)}

								{renderEditableField(
									"Roll Number",
									"rollNumber",
									typeof extractedData?.rollNumber === "string"
										? extractedData.rollNumber
										: "",
								)}
							</div>
						)}
						{docType.includes("english") && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{renderEditableField(
									"Test Name",
									"test_type",
									typeof extractedData?.test_type === "string"
										? extractedData.test_type
										: "",
								)}
								{renderEditableField(
									"Overall Score",
									"overall_score",
									typeof extractedData?.overall_score === "string"
										? extractedData.overall_score
										: "",
								)}
								{renderEditableField(
									"Test Date",
									"test_date",
									typeof extractedData?.test_date === "string"
										? extractedData.test_date
										: "",
									"date",
								)}
								{renderEditableField(
									"Valid Till Date",
									"valid_till_date",
									typeof extractedData?.valid_till_date === "string"
										? extractedData.valid_till_date
										: "",
									"date",
								)}
								{["listening", "reading", "writing", "speaking"].map((field) =>
									typeof extractedData.section_scores === "object" &&
									extractedData.section_scores !== null &&
									field in (extractedData.section_scores as SectionScores)
										? renderEditableField(
												`${field.charAt(0).toUpperCase() + field.slice(1)} Score`,
												field,
												(extractedData.section_scores as SectionScores)[field],
											)
										: null,
								)}
							</div>
						)}

						{docType.includes("moi") && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{renderEditableField(
									"Board Name",
									"board_name",
									typeof extractedData?.board_name === "string"
										? extractedData.board_name
										: "",
									"text",
									true,
								)}
								{renderEditableField(
									"English Marks (out of 100)",
									"english_marks",
									typeof extractedData?.english_marks === "string"
										? extractedData.english_marks
										: extractedData?.english_marks?.toString() || "",
									"number",
								)}
							</div>
						)}
					</div>
				</div>

				<div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
					{!inline && (
						<Button
							variant="outline"
							onClick={onClose}
							className="px-6 py-2 border-gray-300 text-gray-600 hover:bg-gray-50">
							Close
						</Button>
					)}
					<Button
						onClick={() => handleConfirm()}
						className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium"
						disabled={isLoading || hasValidationErrors}>
						{isLoading ? (
							<>
								<Loader2 className="animate-spin" />
								<span>Loading...</span>
							</>
						) : (
							"Confirm"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ConfirmExtractedDetails;
